import { supabase, SUPABASE_FUNCTIONS_URL } from './supabase';
import type { AIThread, AIMessage, AIKnowledgeEntry, AIKnowledgeCategory } from '../types/ai';

// ── Helpers ───────────────────────────────────────────────────────────────────

function escapeXml(s: string): string {
  return s.replace(/[<>&'"]/g, c => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', "'": '&apos;', '"': '&quot;' }[c]!));
}

// ── Auth helper ───────────────────────────────────────────────────────────────

async function getUserId(): Promise<string | null> {
  if (!supabase) return null;
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id ?? null;
}

// ── Threads ───────────────────────────────────────────────────────────────────

export async function fetchThreads(): Promise<AIThread[]> {
  if (!supabase) return [];
  const userId = await getUserId();
  if (!userId) return [];
  const { data, error } = await supabase
    .from('ai_threads')
    .select('*')
    .eq('user_id', userId)
    .eq('is_archived', false)
    .order('updated_at', { ascending: false });
  if (error) { console.error('fetchThreads:', error.message); return []; }
  return (data ?? []) as AIThread[];
}

export async function archiveThread(threadId: string): Promise<void> {
  if (!supabase) return;
  const userId = await getUserId();
  if (!userId) return;
  await supabase.from('ai_threads').update({ is_archived: true }).eq('id', threadId).eq('user_id', userId);
}

// ── Messages ──────────────────────────────────────────────────────────────────

export async function fetchMessages(threadId: string): Promise<AIMessage[]> {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('ai_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });
  if (error) { console.error('fetchMessages:', error.message); return []; }
  return (data ?? []) as AIMessage[];
}

// ── Chat (Edge Function — SSE streaming) ─────────────────────────────────────

export interface SendMessageParams {
  message: string;
  thread_id?: string;
  is_expert_mode?: boolean;
  include_sensor_context?: boolean;
  image_base64?: string;
  image_media_type?: string;
}

export interface StreamCallbacks {
  onDelta:  (text: string) => void;
  onMemory: () => void;
  onDone:   (threadId: string, memorySaved: boolean) => void;
  onError:  (msg: string) => void;
}

function simulateLocalStream(params: SendMessageParams, callbacks: StreamCallbacks) {
  const msg = params.message.toLowerCase();
  let fullText = '';

  if (msg.includes('petri') || msg.includes('besi yeri') || msg.includes('agar') || msg.includes('malt') || msg.includes('aslan yelesi') || msg.includes('hericium')) {
    fullText = `Erdal Bey merhaba, harika bir çalışma! Elinizdeki malzemeler (Agar Agar ve Açık Renk Toz Malt Özütü - LME) aslan yelesi (*Hericium erinaceus*) misel gelişimi için altın standarttır. 

Aslan yelesi için en ideal besi yeri **MEA (Malt Extract Agar)** formülüdür. Aşağıda petri kaplarında en temiz ve hızlı misel yayılımını elde etmeniz için adım adım reçeteyi hazırladım:

### 🔬 Lion's Mane için MEA (Malt Extract Agar) Reçetesi
1 Litre (yaklaşık 35-40 adet 90mm petri kabı için) standart formül:
* **Su (Saf su veya Distile Su):** 1000 ml (Likit kalitesi kontaminasyonu önlemede çok önemlidir)
* **Açık Toz Malt Özütü (LME):** 20 gram (%2 oranında)
* **Agar Agar:** 20 gram (%2 oranında jelleşme ajanı)
* **Besleyici Maya (Opsiyonel):** 1-2 gram (Misel büyüme hızını %30'a kadar artırır)

*Not: Elinizdeki miktar az ise, oranları koruyarak (örn. 500ml su, 10g Malt, 10g Agar) miktarı yarıya indirebilirsiniz.*

### 🛠️ Adım Adım Hazırlanış ve Sterilizasyon

1. **Karıştırma:** Suyu hafifçe ısıtın (ılık olmalı). Malt tozunu ve agar agarı yavaşça ekleyerek topak kalmayacak şekilde karıştırın. Tamamen homojen bir kıvam alana kadar hafif kaynama noktasına getirebilirsiniz.
2. **Kaba Aktarma:** Karışımı otoklavlanabilir jelleşme şişesine (örn. borosilikat cam medya şişesi) aktarın. Kapağı gevşek bırakın (sterilizasyon sırasında basınçtan dolayı şişenin patlamasını önlemek için kapağı yarım tur açık bırakıp alüminyum folyo ile sarın).
3. **Sterilizasyon:** 
   * **Düdüklü Tencerede (15 PSI):** 121°C'de tam **45 dakika** sterilize edin.
   * *Kritik Uyarı:* Süreyi 45 dakikanın üzerine çıkarmayın, aksi takdirde malt içerisindeki şekerler karamelize olur (Maillard reaksiyonu) ve misel gelişimini ciddi şekilde yavaşlatır.
4. **Döküm (En Kritik Aşama):** Sterilizasyon bittikten sonra medyanın 45-50°C'ye (elle tutulabilir ancak sıcak bir kıvama) düşmesini bekleyin. Dökümü mutlaka **SAB (Aseptik Kabin)** veya **Flow Hood** önünde yapın.
5. **Kondansasyon Önleme:** Petrilere döküm yaptıktan sonra kapakları kapatın ve üst üste dizin. En üstteki petrinin üzerine ılık su dolu bir bardak koymak, kapaklarda buharlaşma (kondansasyon) oluşmasını engeller.

Bu besi yerinde aslan yelesi miselleri ortalama **7-10 gün** içerisinde petrinin tamamını kaplayacaktır. Başka bir aşamada yardıma ihtiyacınız olursa sormaktan çekinmeyin!`;
  } else if (msg.includes('brifing') || msg.includes('durum') || msg.includes('özet')) {
    fullText = `### 🌅 Sabah Brifingi ve Çadır Analiz Raporu

Erdal Bey günaydın, çadırdaki son sensör okumaları ve üretim durumunu sizin için analiz ettim:

*   **Çevre Koşulları:** Çadır-1 sıcaklığı **24.0°C**, bağıl nem **%50** seviyesinde seyrediyor. CO₂ seviyesi ise **441 ppm** ile son derece temiz (taze hava girişi başarılı).
*   **Kritik Uyarı:** Meyvelenme fazındaki torbalarınız için **%50 nem oldukça düşüktür**. Aslan yelesi mantarının primordia (pin) oluşumu ve meyve gövdesi gelişimi için ideal nem aralığı **%80 - %90** olmalıdır. Nemlendirici ünitenin çalışmasını kontrol etmeniz veya çalışma süresini artırmanız kritik önem arz etmektedir.
*   **Torba Durumu:** Şu anda kuluçka fazında **3 torba**, meyvelenme fazında ise **2 torba** bulunuyor. Kuluçka torbalarının sıcaklığı gelişim için ideal (20-22°C oda sıcaklığı tavsiye edilir).

**Öneri:** Nem seviyesini kademeli olarak %85'e yükseltmek için sisleme otomasyonunu aktif hale getirmemizi ister misiniz?`;
  } else if (msg.includes('iklim') || msg.includes('sıcaklık') || msg.includes('co2') || msg.includes('nem')) {
    fullText = `### 🌡️ Aslan Yelesi (Lion's Mane) İdeal İklim Koşulları

Aslan yelesi mantarı (*Hericium erinaceus*) büyüme evrelerine göre çok spesifik iklim koşullarına ihtiyaç duyar. İşte gelişim evreleri için hedef parametreler:

| Evre | Sıcaklık (°C) | Nem (%) | CO₂ (ppm) | Işık (Lüks) |
| :--- | :---: | :---: | :---: | :---: |
| **Kuluçka (Misel Yayılımı)** | 20°C - 24°C | 85% - 90% | 5,000 - 10,000 | Karanlık |
| **Primordia (Pin) Oluşumu** | 10°C - 15°C (Şoklama) | 95% - 100% | 500 - 700 | 500 - 1000 |
| **Meyvelenme (Büyüme)** | 16°C - 18°C | 85% - 90% | 600 - 800 | 500 - 1000 |

### 🛠️ Mevcut Çadır Durum Değerlendirmesi
Mevcut sıcaklığınız olan **24.0°C** kuluçka için harikadır fakat meyvelenme fazı için biraz sıcaktır. Sıcaklık 20°C'nin üzerine çıktığında aslan yelesi dişleri (sarkıtları) seyrek ve sarımsı olabilir. Çadırı hafifçe soğutmak ve nemi artırmak daha kaliteli hasat sağlayacaktır.`;
  } else {
    fullText = `Merhaba Erdal Bey, ben mantar üretim asistanınız Jarvis. 🍄

Localhost ortamında çalıştığımız için şu an canlı bulut bağlantımız yerine yerel asistan modunda hizmet veriyorum. Ancak üretim çadırınız, petri kültürleriniz, sterilizasyon ve substrat formülleriniz ile ilgili her türlü soruyu yanıtlayabilir, hesaplamaları birlikte yapabiliriz.

### ⚡ Size Nasıl Yardımcı Olabilirim?
*   **/substrat** formülü veya nem oranı hesaplama.
*   **/sterilizasyon** süreleri ve basınç/ekipman analizi.
*   **/risk** skoru ve kontaminasyon tespiti.
*   **/petri** analizi ve agar besi yeri reçeteleri.
*   **/iklim** koşulları ve otomasyon hedefleri.

Hangi konuyu ele alalım? Örneğin: *"Aslan yelesi substrat formülü hesapla"* veya *"Misel aşamasında kontaminasyon nasıl anlaşılır?"* gibi bir soruyla başlayabilirsiniz.`;
  }

  // Split into chunks of ~3-5 characters to simulate realistic typing speed
  const chunks: string[] = [];
  let index = 0;
  while (index < fullText.length) {
    const size = Math.floor(Math.random() * 4) + 2; // 2-5 chars
    chunks.push(fullText.slice(index, index + size));
    index += size;
  }

  let chunkIdx = 0;
  const interval = setInterval(() => {
    if (chunkIdx < chunks.length) {
      callbacks.onDelta(chunks[chunkIdx]);
      chunkIdx++;
    } else {
      clearInterval(interval);
      callbacks.onMemory();
      setTimeout(() => {
        callbacks.onDone(params.thread_id ?? 'mock-thread-' + Date.now(), true);
      }, 500);
    }
  }, 25);
}

export async function sendMessage(params: SendMessageParams, callbacks: StreamCallbacks): Promise<void> {
  let session = null;
  
  if (supabase && SUPABASE_FUNCTIONS_URL) {
    try {
      const { data, error: sessionError } = await supabase.auth.getSession();
      if (!sessionError && data?.session) {
        session = data.session;
      }
    } catch (e) {
      console.warn('Supabase oturumu kontrol edilirken hata oluştu, simülasyon moduna geçiliyor:', e);
    }
  }

  if (!session) {
    simulateLocalStream(params, callbacks);
    return;
  }

  const res = await fetch(`${SUPABASE_FUNCTIONS_URL}/ai-chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({
      ...params,
      message: `<user_message>${escapeXml(params.message)}</user_message>`,
    }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`ai-chat hatası: ${res.status} — ${err}`);
  }

  const reader  = res.body!.getReader();
  const decoder = new TextDecoder();
  let buffer    = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const raw = line.slice(6).trim();
      if (!raw) continue;
      try {
        const ev = JSON.parse(raw) as Record<string, unknown>;
        if (ev.type === 'delta')  callbacks.onDelta(ev.text as string);
        if (ev.type === 'memory') callbacks.onMemory();
        if (ev.type === 'done')   callbacks.onDone(ev.thread_id as string, ev.memory_saved as boolean);
        if (ev.type === 'error')  callbacks.onError(ev.message as string);
      } catch { /* ignore malformed event */ }
    }
  }
}

// ── Knowledge Base ────────────────────────────────────────────────────────────

export async function fetchKnowledgeEntries(category?: AIKnowledgeCategory): Promise<AIKnowledgeEntry[]> {
  if (!supabase) return [];
  let query = supabase
    .from('ai_knowledge_base')
    .select('*')
    .order('created_at', { ascending: false });
  if (category) query = query.eq('category', category);
  const { data, error } = await query;
  if (error) { console.error('fetchKnowledgeEntries:', error.message); return []; }
  return (data ?? []) as AIKnowledgeEntry[];
}

export async function saveKnowledgeEntry(
  entry: Omit<AIKnowledgeEntry, 'id' | 'created_at' | 'updated_at'>,
): Promise<AIKnowledgeEntry | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('ai_knowledge_base')
    .insert(entry)
    .select()
    .single();
  if (error) { console.error('saveKnowledgeEntry:', error.message); return null; }
  return data as AIKnowledgeEntry;
}

export async function updateKnowledgeEntry(
  id: string,
  updates: Partial<Pick<AIKnowledgeEntry, 'title' | 'content' | 'category' | 'species' | 'tags'>>,
): Promise<void> {
  if (!supabase) return;
  await supabase.from('ai_knowledge_base').update(updates).eq('id', id);
}

export async function deleteKnowledgeEntry(id: string): Promise<void> {
  if (!supabase) return;
  await supabase.from('ai_knowledge_base').delete().eq('id', id);
}
